import { useState, FormEvent } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../../components/base/SEO';

export default function ContactoPage() {
  const [formData, setFormData] = useState({
    tipoVivienda: '',
    municipio: '',
    superficie: '',
    volumen: '',
    ascensor: false,
    escaleras: false,
    distancia: false,
    urgencia: 'normal',
    mueblesValor: '',
    nombre: '',
    telefono: '',
    email: '',
    mensaje: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('tipoVivienda', formData.tipoVivienda);
      formBody.append('municipio', formData.municipio);
      formBody.append('superficie', formData.superficie);
      formBody.append('volumen', formData.volumen);
      
      const accesos = [];
      if (formData.ascensor) accesos.push('Ascensor');
      if (formData.escaleras) accesos.push('Solo escaleras');
      if (formData.distancia) accesos.push('Distancia al camión');
      formBody.append('accesos', accesos.join(', '));
      
      formBody.append('urgencia', formData.urgencia);
      formBody.append('mueblesValor', formData.mueblesValor);
      formBody.append('nombre', formData.nombre);
      formBody.append('telefono', formData.telefono);
      formBody.append('email', formData.email);
      formBody.append('mensaje', formData.mensaje);

      const response = await fetch('https://readdy.ai/api/form/d56e7t13kamldd155ivg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          tipoVivienda: '',
          municipio: '',
          superficie: '',
          volumen: '',
          ascensor: false,
          escaleras: false,
          distancia: false,
          urgencia: 'normal',
          mueblesValor: '',
          nombre: '',
          telefono: '',
          email: '',
          mensaje: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const localBusinessSchema = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": "VACIADOS MARIUS",
    "@id": `${import.meta.env.VITE_SITE_URL || 'https://vaciado-pisos-barcelona.vaciadosmarius.com.es'}/#organization`,
    "url": import.meta.env.VITE_SITE_URL || "https://vaciado-pisos-barcelona.vaciadosmarius.com.es/",
    "telephone": "+34936940041",
    "email": "vaciadosmarius@gmail.com",
    "address": {
      "@type": "PostalAddress",
      "addressLocality": "Barcelona",
      "addressRegion": "Cataluña",
      "addressCountry": "ES"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": "41.3851",
      "longitude": "2.1734"
    },
    "sameAs": "https://share.google/FizsQeezE7RkuyEkt"
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Inicio",
        "item": import.meta.env.VITE_SITE_URL || "https://vaciado-pisos-barcelona.vaciadosmarius.com.es/"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Contacto",
        "item": `${import.meta.env.VITE_SITE_URL || 'https://vaciado-pisos-barcelona.vaciadosmarius.com.es'}/contacto`
      }
    ]
  };

  return (
    <>
      <SEO
        title="Contacto Vaciado Pisos Barcelona | Presupuesto Gratis | VACIADOS MARIUS"
        description="Solicita presupuesto gratis para vaciar tu piso en Barcelona. Llamar 936 94 00 41, WhatsApp 633 759 649 o envía fotos. Respuesta en 24-48h."
        canonical={`${import.meta.env.VITE_SITE_URL || 'https://vaciado-pisos-barcelona.vaciadosmarius.com.es'}/contacto`}
        keywords="contacto vaciado pisos Barcelona, presupuesto gratis, solicitar vaciado, WhatsApp vaciado Barcelona"
        geoRegion="ES-CT"
        geoPlacename="Barcelona"
        geoPosition="41.3851;2.1734"
        jsonLd={[localBusinessSchema, breadcrumbSchema]}
      />

      <main className="pt-20 overflow-x-hidden">
        {/* Hero Section */}
        <section className="bg-gray-50 py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <nav className="text-sm text-gray-600 mb-8">
              <Link to="/" className="hover:text-teal-600 cursor-pointer">Inicio</Link>
              <span className="mx-2">&gt;</span>
              <span className="text-gray-900">Contacto</span>
            </nav>

            <div className="text-center max-w-3xl mx-auto">
              <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-4">
                Contacta con nosotros
              </h1>
              <p className="text-lg sm:text-xl text-gray-600">
                Respuesta en 24-48 horas • Evaluación gratuita • <Link to="/servicios" className="text-teal-600 hover:text-teal-700 font-semibold cursor-pointer">Ver servicios</Link>
              </p>
            </div>
          </div>
        </section>

        {/* Contact Methods Grid */}
        <section className="bg-white py-16 sm:py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-16 sm:mb-20">
              <div className="bg-gray-50 rounded-xl p-4 sm:p-6 text-center hover:shadow-lg transition-shadow">
                <div className="w-16 h-16 flex items-center justify-center bg-teal-100 rounded-full mx-auto mb-4">
                  <i className="ri-phone-line text-teal-600 text-3xl"></i>
                </div>
                <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2">Llámanos directamente</h3>
                <p className="text-xs sm:text-sm text-gray-600 mb-4">
                  Horario: Lun-Vie 8:00-19:00<br />Sáb 9:00-14:00
                </p>
                <p className="text-xl sm:text-2xl font-bold text-gray-900 mb-4 break-all">936 94 00 41</p>
                <a
                  href="tel:+34936940041"
                  className="inline-block px-4 sm:px-6 py-2 sm:py-3 bg-teal-600 text-white rounded-full font-semibold hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer text-sm sm:text-base"
                >
                  LLAMAR AHORA
                </a>
              </div>

              <div className="bg-gray-50 rounded-xl p-4 sm:p-6 text-center hover:shadow-lg transition-shadow">
                <div className="w-16 h-16 flex items-center justify-center bg-green-100 rounded-full mx-auto mb-4">
                  <i className="ri-whatsapp-line text-green-600 text-3xl"></i>
                </div>
                <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2">Envíanos fotos por WhatsApp</h3>
                <p className="text-xs sm:text-sm text-gray-600 mb-4">
                  La forma más rápida de valorar tu caso
                </p>
                <p className="text-xl sm:text-2xl font-bold text-gray-900 mb-4 break-all">633 759 649</p>
                <a
                  href="https://wa.me/34633759649"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block px-4 sm:px-6 py-2 sm:py-3 bg-green-600 text-white rounded-full font-semibold hover:bg-green-700 transition-colors whitespace-nowrap cursor-pointer text-sm sm:text-base"
                >
                  ABRIR WHATSAPP
                </a>
              </div>

              <div className="bg-gray-50 rounded-xl p-4 sm:p-6 text-center hover:shadow-lg transition-shadow">
                <div className="w-16 h-16 flex items-center justify-center bg-blue-100 rounded-full mx-auto mb-4">
                  <i className="ri-mail-line text-blue-600 text-3xl"></i>
                </div>
                <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2">Escríbenos un email</h3>
                <p className="text-xs sm:text-sm text-gray-600 mb-4">
                  Te respondemos en menos de 24 horas
                </p>
                <p className="text-sm sm:text-lg font-bold text-gray-900 mb-4 break-all px-2">vaciadosmarius@gmail.com</p>
                <a
                  href="mailto:vaciadosmarius@gmail.com"
                  className="inline-block px-4 sm:px-6 py-2 sm:py-3 bg-blue-600 text-white rounded-full font-semibold hover:bg-blue-700 transition-colors whitespace-nowrap cursor-pointer text-sm sm:text-base"
                >
                  ENVIAR EMAIL
                </a>
              </div>

              <div className="bg-gray-50 rounded-xl p-4 sm:p-6 text-center hover:shadow-lg transition-shadow">
                <div className="w-16 h-16 flex items-center justify-center bg-orange-100 rounded-full mx-auto mb-4">
                  <i className="ri-google-line text-orange-600 text-3xl"></i>
                </div>
                <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2">Visita nuestro perfil</h3>
                <p className="text-xs sm:text-sm text-gray-600 mb-4">
                  Lee opiniones de otros clientes
                </p>
                <div className="mb-4">
                  <div className="flex items-center justify-center space-x-1 mb-2">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <i key={star} className="ri-star-fill text-yellow-500"></i>
                    ))}
                  </div>
                  <p className="text-xs sm:text-sm text-gray-600">4.9/5 estrellas</p>
                </div>
                <a
                  href="https://share.google/FizsQeezE7RkuyEkt"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block px-4 sm:px-6 py-2 sm:py-3 bg-orange-600 text-white rounded-full font-semibold hover:bg-orange-700 transition-colors whitespace-nowrap cursor-pointer text-sm sm:text-base"
                >
                  VER PERFIL GOOGLE
                </a>
              </div>
            </div>

            {/* Contact Form Section */}
            <div className="grid lg:grid-cols-3 gap-8 sm:gap-12">
              {/* Form */}
              <div className="lg:col-span-2">
                <div className="bg-white border border-gray-200 rounded-2xl shadow-lg p-6 sm:p-8">
                  <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-6">
                    Solicita presupuesto sin compromiso
                  </h2>

                  <form id="contact-form" data-readdy-form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Tipo de vivienda
                        </label>
                        <select
                          name="tipoVivienda"
                          value={formData.tipoVivienda}
                          onChange={(e) => setFormData({ ...formData, tipoVivienda: e.target.value })}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                          required
                        >
                          <option value="">Selecciona...</option>
                          <option value="Piso">Piso</option>
                          <option value="Casa">Casa</option>
                          <option value="Local">Local</option>
                          <option value="Trastero">Trastero</option>
                          <option value="Otro">Otro</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Municipio
                        </label>
                        <input
                          type="text"
                          name="municipio"
                          value={formData.municipio}
                          onChange={(e) => setFormData({ ...formData, municipio: e.target.value })}
                          placeholder="Ej: Barcelona, L'Hospitalet..."
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                          required
                        />
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Superficie aproximada (m²)
                        </label>
                        <input
                          type="number"
                          name="superficie"
                          value={formData.superficie}
                          onChange={(e) => setFormData({ ...formData, superficie: e.target.value })}
                          placeholder="Ej: 60"
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                          required
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Volumen estimado
                        </label>
                        <select
                          name="volumen"
                          value={formData.volumen}
                          onChange={(e) => setFormData({ ...formData, volumen: e.target.value })}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                          required
                        >
                          <option value="">Selecciona...</option>
                          <option value="Poco amueblado">Poco amueblado</option>
                          <option value="Normal">Normal</option>
                          <option value="Muy lleno">Muy lleno</option>
                          <option value="Síndrome Diógenes">Síndrome Diógenes</option>
                        </select>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-3">
                        Accesos
                      </label>
                      <div className="space-y-2">
                        <label className="flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            name="ascensor"
                            checked={formData.ascensor}
                            onChange={(e) => setFormData({ ...formData, ascensor: e.target.checked })}
                            className="w-5 h-5 text-teal-600 border-gray-300 rounded focus:ring-teal-500 cursor-pointer"
                          />
                          <span className="ml-3 text-gray-700">Ascensor</span>
                        </label>
                        <label className="flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            name="escaleras"
                            checked={formData.escaleras}
                            onChange={(e) => setFormData({ ...formData, escaleras: e.target.checked })}
                            className="w-5 h-5 text-teal-600 border-gray-300 rounded focus:ring-teal-500 cursor-pointer"
                          />
                          <span className="ml-3 text-gray-700">Solo escaleras</span>
                        </label>
                        <label className="flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            name="distancia"
                            checked={formData.distancia}
                            onChange={(e) => setFormData({ ...formData, distancia: e.target.checked })}
                            className="w-5 h-5 text-teal-600 border-gray-300 rounded focus:ring-teal-500 cursor-pointer"
                          />
                          <span className="ml-3 text-gray-700">Distancia al camión</span>
                        </label>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-3">
                        Urgencia
                      </label>
                      <div className="space-y-2">
                        <label className="flex items-center cursor-pointer">
                          <input
                            type="radio"
                            name="urgencia"
                            value="normal"
                            checked={formData.urgencia === 'normal'}
                            onChange={(e) => setFormData({ ...formData, urgencia: e.target.value })}
                            className="w-5 h-5 text-teal-600 border-gray-300 focus:ring-teal-500 cursor-pointer"
                          />
                          <span className="ml-3 text-gray-700">Normal (7-15 días)</span>
                        </label>
                        <label className="flex items-center cursor-pointer">
                          <input
                            type="radio"
                            name="urgencia"
                            value="urgente"
                            checked={formData.urgencia === 'urgente'}
                            onChange={(e) => setFormData({ ...formData, urgencia: e.target.value })}
                            className="w-5 h-5 text-teal-600 border-gray-300 focus:ring-teal-500 cursor-pointer"
                          />
                          <span className="ml-3 text-gray-700">Urgente (3-7 días)</span>
                        </label>
                        <label className="flex items-center cursor-pointer">
                          <input
                            type="radio"
                            name="urgencia"
                            value="muy-urgente"
                            checked={formData.urgencia === 'muy-urgente'}
                            onChange={(e) => setFormData({ ...formData, urgencia: e.target.value })}
                            className="w-5 h-5 text-teal-600 border-gray-300 focus:ring-teal-500 cursor-pointer"
                          />
                          <span className="ml-3 text-gray-700">Muy urgente (1-2 días)</span>
                        </label>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        ¿Hay muebles de valor?
                      </label>
                      <textarea
                        name="mueblesValor"
                        value={formData.mueblesValor}
                        onChange={(e) => setFormData({ ...formData, mueblesValor: e.target.value })}
                        placeholder="Describe si hay muebles de diseño, antigüedades +100 años, marca, estado..."
                        rows={3}
                        maxLength={500}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 resize-none"
                      ></textarea>
                      <p className="text-xs text-gray-500 mt-1">Máximo 500 caracteres</p>
                    </div>

                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Nombre
                        </label>
                        <input
                          type="text"
                          name="nombre"
                          value={formData.nombre}
                          onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                          required
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Teléfono
                        </label>
                        <input
                          type="tel"
                          name="telefono"
                          value={formData.telefono}
                          onChange={(e) => setFormData({ ...formData, telefono: e.target.value })}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                          required
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Email
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Mensaje adicional
                      </label>
                      <textarea
                        name="mensaje"
                        value={formData.mensaje}
                        onChange={(e) => setFormData({ ...formData, mensaje: e.target.value })}
                        rows={4}
                        maxLength={500}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 resize-none"
                      ></textarea>
                      <p className="text-xs text-gray-500 mt-1">Máximo 500 caracteres</p>
                    </div>

                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full px-6 sm:px-8 py-3 sm:py-4 bg-teal-600 text-white rounded-full text-base sm:text-lg font-semibold hover:bg-teal-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                    >
                      {isSubmitting ? 'ENVIANDO...' : 'ENVIAR SOLICITUD'}
                    </button>

                    {submitStatus === 'success' && (
                      <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-green-800">
                        <i className="ri-checkbox-circle-line text-xl mr-2"></i>
                        ¡Solicitud enviada correctamente! Te contactaremos en 24-48 horas.
                      </div>
                    )}

                    {submitStatus === 'error' && (
                      <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-800">
                        <i className="ri-error-warning-line text-xl mr-2"></i>
                        Error al enviar. Por favor, inténtalo de nuevo o llámanos.
                      </div>
                    )}

                    <p className="text-xs sm:text-sm text-gray-600 text-center">
                      O envíanos fotos directamente por WhatsApp para valoración más rápida
                    </p>
                  </form>
                </div>
              </div>

              {/* Info Box */}
              <div className="lg:col-span-1">
                <div className="bg-teal-50 rounded-2xl p-6 sm:p-8 sticky top-24">
                  <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-6">
                    Qué necesitamos saber
                  </h3>

                  <ul className="space-y-4 mb-8">
                    {[
                      'Fotos o vídeo del piso (general y detalles)',
                      'Ubicación y accesos (ascensor, escaleras)',
                      'Superficie aproximada',
                      'Si hay muebles de diseño o antigüedades',
                      'Cuándo necesitas el vaciado'
                    ].map((item, index) => (
                      <li key={index} className="flex items-start space-x-3">
                        <i className="ri-checkbox-circle-fill text-teal-600 text-xl flex-shrink-0 mt-0.5"></i>
                        <span className="text-gray-700">{item}</span>
                      </li>
                    ))}
                  </ul>

                  <div className="bg-amber-100 border-l-4 border-amber-500 rounded-lg p-4 mb-6">
                    <p className="text-sm text-gray-800 flex items-start">
                      <i className="ri-lightbulb-line text-amber-600 text-xl mr-2 flex-shrink-0 mt-0.5"></i>
                      <span>
                        <strong>Consejo:</strong> No tires nada antes de consultarnos. Podemos identificar objetos con valor que compensen el coste. Lee más sobre <a href="/servicios#compra-muebles" className="text-teal-600 hover:text-teal-700 font-semibold cursor-pointer">qué muebles compramos</a>.
                      </span>
                    </p>
                  </div>

                  <a
                    href="https://wa.me/34633759649"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center space-x-2 w-full px-4 sm:px-6 py-3 sm:py-4 bg-green-600 text-white rounded-full font-semibold hover:bg-green-700 transition-colors whitespace-nowrap cursor-pointer text-sm sm:text-base"
                  >
                    <i className="ri-whatsapp-fill text-lg sm:text-xl flex-shrink-0"></i>
                    <span>ENVIAR FOTOS WHATSAPP</span>
                  </a>
                  <div className="mt-4 text-center">
                    <p className="text-xs text-gray-600">
                      ¿Tienes dudas? Consulta nuestras <a href="/faq" className="text-teal-600 hover:text-teal-700 font-semibold cursor-pointer">preguntas frecuentes</a>
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="bg-gradient-to-b from-amber-900 to-amber-950 py-16 sm:py-20">
          <div className="max-w-4xl mx-auto px-4 text-center">
            <p className="text-lg sm:text-xl text-white/90 mb-6">
              ¿Prefieres hablar directamente?
            </p>
            <p className="text-4xl sm:text-5xl font-bold text-white mb-6 sm:mb-8 break-all px-4">
              936 94 00 41
            </p>
            <a
              href="tel:+34936940041"
              className="inline-block px-6 sm:px-8 py-3 sm:py-4 bg-white text-gray-900 rounded-full font-semibold hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer text-sm sm:text-base"
            >
              LLAMAR AHORA
            </a>
            <p className="text-white/80 text-xs sm:text-sm mt-6 break-all px-4">
              O escríbenos por WhatsApp: 633 759 649
            </p>
            <div className="mt-8 pt-8 border-t border-white/20">
              <p className="text-white/90 text-sm mb-4">
                Conoce más sobre nosotros
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <Link to="/empresa" className="text-white hover:text-teal-400 font-semibold text-sm cursor-pointer">
                  Nuestra empresa
                </Link>
                <span className="text-white/40">•</span>
                <Link to="/servicios" className="text-white hover:text-teal-400 font-semibold text-sm cursor-pointer">
                  Servicios
                </Link>
                <span className="text-white/40">•</span>
                <a href="/#cobertura" className="text-white hover:text-teal-400 font-semibold text-sm cursor-pointer">
                  Zonas de cobertura
                </a>
                <span className="text-white/40">•</span>
                <Link to="/faq" className="text-white hover:text-teal-400 font-semibold text-sm cursor-pointer">
                  Preguntas frecuentes
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>
    </>
  );
}
